import React, { useState } from 'react';
import { Coins, UsersThree, CheckCircle } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND, formatDate, toYmd } from '_helpers';
import { PageHeader, Bezel, Table, StatCard, StatusPill, Button, Modal, PeriodPicker, MotionReveal } from '_ui';

const now = new Date();

export function BangLuong() {
    const [view, setView] = useState({ mode: 'month', date: toYmd(now), period: { month: now.getMonth() + 1, year: now.getFullYear() } });
    const query = view.mode === 'day' ? `date=${view.date}` : `month=${view.period.month}&year=${view.period.year}`;
    const { data, loading, refetch } = useFetch(`/api/payouts?${query}`);
    const [selected, setSelected] = useState(null);

    const employees = data?.employees || [];
    const totalToPay = employees.reduce((s, e) => s + e.totalCommission, 0);
    const paidCount = employees.filter(e => e.payoutStatus === 'paid').length;

    const columns = [
        { key: 'name', header: 'Nhân viên', render: r => (
            <div>
                <p className="font-medium text-espresso">{r.employee.name}</p>
                <p className="text-xs text-espresso-soft/70">{r.employee.email}</p>
            </div>
        ) },
        { key: 'count', header: 'Hoá đơn', align: 'right', render: r => <span className="tnum text-espresso-soft">{r.count}</span> },
        { key: 'revenue', header: 'Doanh thu', align: 'right', render: r => <span className="tnum">{formatVND(r.totalRevenue)}</span> },
        { key: 'commission', header: 'Hoa hồng', align: 'right', render: r => <span className="tnum font-semibold text-sage-deep">{formatVND(r.totalCommission)}</span> },
        { key: 'status', header: 'Trạng thái', align: 'right', render: r => <StatusPill status={r.payoutStatus} paidLabel="Đã trả" pendingLabel="Chưa trả" /> },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Quản lý"
                title="Bảng lương"
                description="Doanh thu và hoa hồng sơ bộ của từng nhân viên. Bấm vào một nhân viên để xem chi tiết và xác nhận trả lương."
                actions={<PeriodPicker value={view} onChange={setView} />}
            />

            <div className="grid gap-5 sm:grid-cols-3">
                <MotionReveal><StatCard label="Hoa hồng phải trả" value={formatVND(totalToPay)} icon={Coins} accent="sage" /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Số nhân viên" value={employees.length} icon={UsersThree} /></MotionReveal>
                <MotionReveal delay={0.1}><StatCard label="Đã trả lương" value={`${paidCount}/${employees.length}`} /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={employees} keyField={null} loading={loading}
                    onRowClick={r => setSelected(r)} empty="Chưa có nhân viên nào." />
            </Bezel>

            {selected && (
                <PayoutDetailModal
                    row={selected}
                    view={view}
                    onClose={() => setSelected(null)}
                    onPaid={() => { setSelected(null); refetch(); }}
                />
            )}
        </div>
    );
}

function PayoutDetailModal({ row, view, onClose, onPaid }) {
    const id = row.employee._id;
    const query = view.mode === 'day' ? `date=${view.date}` : `month=${view.period.month}&year=${view.period.year}`;
    const { data, loading } = useFetch(`/api/payouts/${id}?${query}`);
    const api = useApi();
    const alert = useAlertActions();
    const [paying, setPaying] = useState(false);

    const detail = data || row;
    const alreadyPaid = (detail.payoutStatus || row.payoutStatus) === 'paid';
    const commission = detail.totalCommission ?? row.totalCommission;

    async function confirmPay() {
        setPaying(true);
        const res = await api.patch(`/api/payouts/${id}/pay`, { month: view.period.month, year: view.period.year });
        setPaying(false);
        if (res.ok) { alert.success('Đã xác nhận trả lương', `${row.employee.name} · ${formatVND(res.data.totalCommission)}`); onPaid(); }
        else { alert.error('Không thể thanh toán', res.error); if (res.status === 409) onPaid(); }
    }

    const columns = [
        { key: 'paidAt', header: 'Ngày', render: r => <span className="tnum text-espresso-soft">{formatDate(r.paidAt || r.createdAt)}</span> },
        { key: 'customer', header: 'Khách', render: r => r.customerName || '—' },
        { key: 'total', header: 'Doanh thu', align: 'right', render: r => <span className="tnum">{formatVND(r.totalPrice)}</span> },
        { key: 'commission', header: 'Hoa hồng', align: 'right', render: r => <span className="tnum text-sage-deep">{formatVND(r.totalCommission)}</span> },
    ];

    const eyebrow = view.mode === 'day' ? `Ngày ${formatDate(view.date)}` : `Tháng ${view.period.month}, ${view.period.year}`;

    return (
        <Modal open onClose={onClose} eyebrow={eyebrow} title={row.employee.name} size="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>Đóng</Button>
                    {view.mode === 'month' && (
                        <Button icon={CheckCircle} onClick={confirmPay} loading={paying}
                            disabled={alreadyPaid || commission <= 0}>
                            {alreadyPaid ? 'Đã trả lương' : 'Xác nhận trả lương'}
                        </Button>
                    )}
                </>
            }>
            <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                    <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-espresso-soft/70">
                        {view.mode === 'day' ? 'Hoa hồng trong ngày' : 'Hoa hồng tháng này'}
                    </span>
                    <p className="mt-2 font-serif text-4xl text-sage-deep tnum">{formatVND(commission)}</p>
                    <p className="mt-1 text-sm text-espresso-soft/80">Doanh thu {formatVND(detail.totalRevenue ?? row.totalRevenue)}</p>
                </div>
                <StatusPill status={alreadyPaid ? 'paid' : 'pending'} paidLabel="Đã trả" pendingLabel="Chưa trả" />
            </div>
            <div className="-mx-1 max-h-72 overflow-y-auto rounded-2xl ring-1 ring-espresso/[0.07]">
                <Table columns={columns} rows={detail.invoices || []} loading={loading} empty="Chưa có hoá đơn đã thanh toán." />
            </div>
        </Modal>
    );
}
